import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ModaleCalendrier } from '../components/ModaleCalendrier';
import { ParametresRemuneration } from '../components/formations/ParametresRemuneration';
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Info,
  Layers,
  Pencil,
  Power,
  Search,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import {
  BoutonMint,
  BoutonPrincipal,
  BoutonSecondaire,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import {
  apercuDureeHebdomadaire,
  DUREES_CYCLE,
  libelleDuree,
  libelleDureeHebdomadaire,
  libelleRangAnnee,
  type HorairesSaisis,
} from '../lib/cycle-formation';
import {
  useFormations,
  useModifierFormation,
  useSupprimerFormation,
  type EntreeModificationFormation,
  type FormationParametree,
  type ModuleSaisi,
} from '../lib/useFormations';

type FiltreEtat = 'TOUTES' | 'ACTIVES' | 'INACTIVES';

/** État local du formulaire de formation (champs numériques modélisés avec '' pour permettre un état vierge). */
export interface BrouillonFormation {
  code: string;
  titre: string;
  niveau: string;
  heures: number | '';
  codeDiplome: string;
  rncp: string;
  heuresEcrits: number | '';
  heuresEcf: number | '';
  /** Lu, jamais écrit ici : le seuil se règle par la direction, dans « Paramétrage » (TF-435). */
  seuilAbsencesDemiJournees: number | '';
  actif: boolean;
  modules: ModuleSaisi[];
  /** Un code d'émargement est déjà posé (sa valeur n'est jamais relue). */
  codeEmargementDefini: boolean;
  /** Nouveau code saisi ; vide, le code en place ne bouge pas. */
  codeEmargement: string;
  retirerCodeEmargement: boolean;
  /** Durée théorique du cycle ; '' tant qu'elle n'est pas choisie. */
  dureeAnnees: number | '';
  horaires: HorairesSaisis;
}

/** Récupération intégrale et fidèle des données d'une formation existante pour modification. */
export function versBrouillon(formation: FormationParametree): BrouillonFormation {
  return {
    code: formation.code,
    titre: formation.titre,
    niveau: formation.niveau,
    heures: formation.heures,
    codeDiplome: formation.codeDiplome,
    rncp: formation.rncp,
    heuresEcrits: formation.heuresEcrits,
    heuresEcf: formation.heuresEcf,
    seuilAbsencesDemiJournees: formation.seuilAbsencesDemiJournees,
    actif: formation.actif,
    modules: formation.modules.map((module) => ({
      id: module.id,
      libelle: module.libelle,
      heures: module.heures === 0 ? '' : module.heures,
    })),
    codeEmargementDefini: formation.codeEmargement.defini,
    codeEmargement: '',
    retirerCodeEmargement: false,
    dureeAnnees: formation.dureeAnnees ?? '',
    horaires: {
      heureDebutMatin: formation.horaires.heureDebutMatin ?? '',
      heureFinMatin: formation.horaires.heureFinMatin ?? '',
      heureDebutApresMidi: formation.horaires.heureDebutApresMidi ?? '',
      heureFinApresMidi: formation.horaires.heureFinApresMidi ?? '',
      joursParSemaine: formation.horaires.joursParSemaine ?? '',
    },
  };
}

/**
 * Écran « Formations & programmes » — pôle administratif et direction (CDC §4.4).
 *
 * Les formations dont le programme est validé, et leurs réglages : durée du
 * cycle, horaires, code d'émargement, calendrier, disponibilité. Le programme
 * lui-même (intitulé, niveau, codes, volumes, blocs) se crée, se modifie et se
 * valide dans « Ingénierie pédagogique » (TF-472) : ici il se lit.
 */
export function AdmFormationsPage(): JSX.Element {
  const { data, isLoading, error } = useFormations();
  const { peutAcceder } = useAuth();
  const lienIngenierie = peutAcceder('ped-ingenierie');
  const modifier = useModifierFormation();
  const supprimer = useSupprimerFormation();
  const toast = useToast();

  const [edition, setEdition] = useState<{ brouillon: BrouillonFormation; id: string } | null>(null);
  const [aSupprimer, setASupprimer] = useState<FormationParametree | null>(null);
  const [recherche, setRecherche] = useState('');
  const [filtreEtat, setFiltreEtat] = useState<FiltreEtat>('TOUTES');
  // La vue globale de la direction ouvre le programme d'une formation : `?formation=<id>`.
  const [parametres] = useSearchParams();
  const [formationDeplieeId, setFormationDeplieeId] = useState<string | null>(() => parametres.get('formation'));
  // Le programme s'affiche sous le catalogue : on y descend une fois, à l'arrivée.
  const programmeAtteint = useRef(false);
  useEffect(() => {
    if (programmeAtteint.current || !parametres.get('formation') || !data) return;
    programmeAtteint.current = true;
    document.getElementById('programme-formation')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [data, parametres]);
  const [calendrierCibleId, setCalendrierCibleId] = useState<string | null>(null);
  /**
   * Deux sujets vivent sur cet écran : le catalogue des formations, et les
   * paramètres qui servent aux calculs. Les empiler sur une seule page rendrait
   * le catalogue inatteignable ; deux écrans sépareraient ce que le pôle
   * administratif règle au même moment.
   */
  const [onglet, setOnglet] = useState<'catalogue' | 'remuneration'>('catalogue');

  const filtree = useMemo(() => {
    const formations = data?.formations ?? [];
    const terme = recherche.trim().toLowerCase();
    return formations.filter((formation) => {
      if (filtreEtat === 'ACTIVES' && !formation.actif) return false;
      if (filtreEtat === 'INACTIVES' && formation.actif) return false;
      if (terme.length === 0) return true;
      return (
        formation.titre.toLowerCase().includes(terme) ||
        formation.code.toLowerCase().includes(terme) ||
        formation.niveau.toLowerCase().includes(terme) ||
        formation.rncp.toLowerCase().includes(terme) ||
        formation.codeDiplome.toLowerCase().includes(terme)
      );
    });
  }, [data, recherche, filtreEtat]);

  if (error) {
    return (
      <div className="mx-auto max-w-[1360px] p-4 md:p-6">
        <MessageErreur
          message={
            error instanceof ErreurApi
              ? error.message
              : 'Les formations n’ont pas pu être chargées.'
          }
        />
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-[1360px] p-4 md:p-6">
        <Chargement libelle="Chargement des formations…" />
      </div>
    );
  }

  // Un identifiant inconnu — formation supprimée depuis le lien — n'ouvre rien.
  const formationDepliee = data.formations.find((f) => f.id === formationDeplieeId);
  const enEcriture = modifier.isPending || supprimer.isPending;
  const erreurEcriture = [modifier.error, supprimer.error].find(
    (candidate): candidate is ErreurApi => candidate instanceof ErreurApi,
  );
  const filtreActif = recherche.trim().length > 0 || filtreEtat !== 'TOUTES';

  const enregistrer = (): void => {
    if (!edition) return;
    const { brouillon, id } = edition;
    // Le code d'émargement ne part que s'il change : il n'est jamais relu, donc
    // jamais renvoyé tel quel.
    const codeEmargement = brouillon.retirerCodeEmargement
      ? { codeEmargement: null }
      : brouillon.codeEmargement
        ? { codeEmargement: brouillon.codeEmargement }
        : {};
    const reglages: EntreeModificationFormation = {
      actif: brouillon.actif,
      dureeAnnees: brouillon.dureeAnnees === '' ? null : brouillon.dureeAnnees,
      heureDebutMatin: brouillon.horaires.heureDebutMatin || null,
      heureFinMatin: brouillon.horaires.heureFinMatin || null,
      heureDebutApresMidi: brouillon.horaires.heureDebutApresMidi || null,
      heureFinApresMidi: brouillon.horaires.heureFinApresMidi || null,
      joursParSemaine:
        brouillon.horaires.joursParSemaine === '' ? null : Number(brouillon.horaires.joursParSemaine),
      ...codeEmargement,
    };
    modifier.mutate(
      { id, ...reglages },
      {
        onSuccess: () => {
          setEdition(null);
          toast('Réglages de la formation enregistrés');
        },
      },
    );
  };

  const basculerActif = (formation: FormationParametree): void => {
    modifier.mutate(
      { id: formation.id, actif: !formation.actif },
      {
        onSuccess: () =>
          toast(
            formation.actif
              ? `« ${formation.titre} » n’est plus proposée comme formation visée`
              : `« ${formation.titre} » est de nouveau proposée comme formation visée`,
          ),
      },
    );
  };

  const basculerDepliee = (id: string): void => {
    setFormationDeplieeId((courant) => (courant === id ? null : id));
  };

  const actives = data.formations.filter((formation) => formation.actif);
  const inactives = data.formations.filter((formation) => !formation.actif);
  const heuresActives = actives.reduce((somme, formation) => somme + formation.heures, 0);
  const dossiers = data.formations.reduce(
    (somme, formation) => somme + formation.rattachements.dossiers,
    0,
  );

  return (
    <div className="mx-auto max-w-[1360px] space-y-6 p-4 md:p-6">
      {/* En-tête de page standardisé Codialis */}
      <EnteteEcran
        titre="Formations & programmes"
        sousTitre="Formations validées : cycle, horaires, émargement, calendrier et disponibilité · le programme se construit et se valide dans « Ingénierie pédagogique »"
        actions={
          lienIngenierie ? (
            <Link
              to="/ped-ingenierie"
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              <GraduationCap className="h-4 w-4" aria-hidden="true" />
              Ingénierie pédagogique
            </Link>
          ) : undefined
        }
      />

      <nav aria-label="Sections de l’écran" className="flex flex-wrap gap-2">
        {(
          [
            ['catalogue', 'Catalogue des formations'],
            ['remuneration', 'Paramètres de rémunération'],
          ] as const
        ).map(([cle, libelle]) => (
          <button
            key={cle}
            type="button"
            onClick={() => setOnglet(cle)}
            aria-current={onglet === cle ? 'page' : undefined}
            className={`min-h-[44px] rounded-xl border px-4 py-2 text-sm font-semibold transition ${
              onglet === cle
                ? 'border-codialis-navy bg-codialis-navy text-white'
                : 'border-gray-200 bg-white text-codialis-navy hover:border-codialis-sky'
            }`}
          >
            {libelle}
          </button>
        ))}
      </nav>

      {onglet === 'remuneration' ? <ParametresRemuneration /> : null}

      {erreurEcriture && <MessageErreur message={erreurEcriture.message} />}

      {onglet === 'catalogue' && (
        <>
      {/* KPI / Indicateurs de synthèse */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <Compteur
          libelle="Formations proposées"
          valeur={actives.length}
          detail="Sélectionnables sur les dossiers candidats"
          ton="navy"
        />
        <Compteur
          libelle="Volume cumulé"
          valeur={`${heuresActives} h`}
          detail="Heures de formation au catalogue actif"
          ton="blue"
        />
        <Compteur
          libelle="Dossiers rattachés"
          valeur={dossiers}
          detail="Dossiers et contrats en cours"
          ton="warm"
        />
        <Compteur
          libelle="Formations désactivées"
          valeur={inactives.length}
          detail="Retirées du choix, historiques préservés"
          ton="mint"
        />
      </div>

      {/* Barre de recherche et filtres */}
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-card">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <label className="relative min-w-0 flex-1" htmlFor="recherche-formation">
            <span className="sr-only">Rechercher une formation</span>
            <Search
              className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray"
              aria-hidden="true"
            />
            <input
              id="recherche-formation"
              type="search"
              value={recherche}
              onChange={(evenement) => setRecherche(evenement.target.value)}
              placeholder="Rechercher par intitulé, code, RNCP, code diplôme…"
              className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-10 pr-3.5 text-sm text-codialis-navy placeholder:text-gray-400 focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20 transition"
            />
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <Segments<FiltreEtat>
              libelle="Filtrer par état de la formation"
              valeur={filtreEtat}
              onChange={setFiltreEtat}
              options={[
                { valeur: 'TOUTES', libelle: `Toutes (${data.formations.length})` },
                { valeur: 'ACTIVES', libelle: `Proposées (${actives.length})` },
                { valeur: 'INACTIVES', libelle: `Désactivées (${inactives.length})` },
              ]}
            />
          </div>
        </div>

        {filtreActif && (
          <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3 text-xs text-codialis-gray">
            <span className="inline-flex items-center gap-1.5 font-medium text-codialis-navy">
              <Layers className="h-3.5 w-3.5 text-codialis-gray" aria-hidden="true" />
              {filtree.length} formation{filtree.length > 1 ? 's' : ''} trouvée{filtree.length > 1 ? 's' : ''} sur {data.formations.length}
            </span>
            <button
              type="button"
              onClick={() => {
                setRecherche('');
                setFiltreEtat('TOUTES');
              }}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold text-codialis-sky transition hover:bg-gray-100"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              Effacer les filtres
            </button>
          </div>
        )}
      </div>

      {/* Tableau / Référentiel des formations */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="font-display text-base font-bold text-codialis-navy">
              Référentiel des formations visées
            </h2>
            <p className="mt-0.5 text-xs text-codialis-gray">
              Formations enregistrées et programmes associés aux dossiers
            </p>
          </div>
          <span className="rounded-lg bg-codialis-sand px-2.5 py-1 font-mono text-xs font-semibold text-codialis-navy">
            {filtree.length} / {data.formations.length}
          </span>
        </div>

        {filtree.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 sm:px-6 py-14 text-center">
            <GraduationCap className="h-9 w-9 text-gray-400" aria-hidden="true" />
            <p className="text-sm font-semibold text-codialis-navy">
              {filtreActif ? 'Aucune formation ne correspond aux critères' : 'Aucune formation enregistrée'}
            </p>
            <p className="max-w-md text-xs text-codialis-gray">
              {filtreActif
                ? 'Modifiez ou effacez vos filtres pour retrouver l’ensemble du référentiel.'
                : 'Un programme validé dans « Ingénierie pédagogique » apparaît ici, prêt à être réglé.'}
            </p>
            {filtreActif && (
              <BoutonSecondaire
                onClick={() => {
                  setRecherche('');
                  setFiltreEtat('TOUTES');
                }}
              >
                Réinitialiser les filtres
              </BoutonSecondaire>
            )}
          </div>
        ) : (
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[1020px] text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/75 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                  <th className="px-5 py-3.5">Formation</th>
                  <th className="px-4 py-3.5">Niveau & BPF</th>
                  <th className="px-4 py-3.5">Volumes</th>
                  <th className="px-4 py-3.5">Programme</th>
                  <th className="px-4 py-3.5">Rattachements</th>
                  <th className="px-4 py-3.5">Statut</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {filtree.map((formation) => {
                  const estDepliee = formationDeplieeId === formation.id;
                  const ecart = formation.heuresModules - formation.heures;
                  const aAvertissement = ecart !== 0 && formation.modules.length > 0;

                  return (
                    <tr
                      key={formation.id}
                      className={`group transition hover:bg-gray-50/70 ${
                        !formation.actif ? 'bg-gray-50/40 opacity-80' : ''
                      }`}
                    >
                      {/* Colonne Formation & Identifiants */}
                      <td className="px-5 py-4 align-top">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2">
                            <span className="inline-block rounded-md bg-codialis-navy/10 px-2 py-0.5 font-mono text-xs font-bold text-codialis-navy">
                              {formation.code}
                            </span>
                            {formation.socle && (
                              <Pastille texte="Socle" ton="info" />
                            )}
                          </div>
                          <span className="font-semibold text-codialis-navy leading-snug">
                            {formation.titre}
                          </span>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-codialis-gray">
                            <span>
                              Diplôme :{' '}
                              <strong className="font-mono font-medium text-codialis-navy">
                                {formation.codeDiplome}
                              </strong>
                            </span>
                            <span>&bull;</span>
                            <span>
                              RNCP :{' '}
                              <strong className="font-mono font-medium text-codialis-navy">
                                {formation.rncp}
                              </strong>
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Colonne Niveau */}
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex self-start rounded-md bg-codialis-sand px-2.5 py-1 text-xs font-semibold text-codialis-navy">
                            {formation.niveau}
                          </span>
                          {formation.niveauBpf !== null ? (
                            <span className="text-[11px] text-codialis-gray">
                              Niveau BPF : <strong className="text-codialis-navy">{formation.niveauBpf}</strong>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] text-amber-700">
                              <AlertTriangle className="h-3 w-3 shrink-0" />
                              BPF indéterminé
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Colonne Volumes horaires */}
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-semibold text-codialis-navy">
                            {formation.heures} h
                          </span>
                          <span className="text-xs text-codialis-gray">
                            Écrits : {formation.heuresEcrits} h &bull; ECF : {formation.heuresEcf} h
                          </span>
                          <span className="text-[11px] text-codialis-gray">
                            Seuil alerte : {formation.seuilAbsencesDemiJournees} demi-j.
                          </span>
                          {/* Durée du cycle visible sans ouvrir la fiche : une
                              durée manquante se repère dans la liste. */}
                          {formation.dureeAnnees ? (
                            <span className="text-xs font-semibold text-codialis-navy">
                              Durée : {libelleDuree(formation.dureeAnnees)}
                              {formation.dureeHebdomadaire
                                ? ` · ${formation.dureeHebdomadaire.libelle} / sem.`
                                : ''}
                            </span>
                          ) : (
                            <span className="text-xs font-semibold text-amber-700">
                              Durée à renseigner
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Colonne Programme / Modules */}
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col gap-1.5">
                          <button
                            type="button"
                            onClick={() => basculerDepliee(formation.id)}
                            aria-expanded={estDepliee}
                            className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition ${
                              estDepliee
                                ? 'border-codialis-navy bg-codialis-navy text-white'
                                : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                            }`}
                          >
                            <Layers className="h-3.5 w-3.5" aria-hidden="true" />
                            <span>
                              {formation.modules.length} module{formation.modules.length > 1 ? 's' : ''}
                            </span>
                            {estDepliee ? (
                              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                            )}
                          </button>
                          {aAvertissement && (
                            <span
                              title={`Écart de volume : ${formation.heuresModules} h saisies vs ${formation.heures} h prévues`}
                              className="inline-flex items-center gap-1 text-[11px] text-amber-700"
                            >
                              <AlertTriangle className="h-3 w-3 shrink-0" />
                              {formation.heuresModules} h saisies ({ecart > 0 ? `+${ecart}` : ecart} h)
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Colonne Dossiers rattachés */}
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col gap-0.5 text-xs text-codialis-gray">
                          <span className="font-semibold text-codialis-navy">
                            {formation.rattachements.dossiers} dossier{formation.rattachements.dossiers > 1 ? 's' : ''}
                          </span>
                          <span>{formation.rattachements.sessions} session(s)</span>
                          <span>{formation.rattachements.contrats} contrat(s)</span>
                        </div>
                      </td>

                      {/* Colonne Statut */}
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col items-start gap-1">
                          {formation.actif ? (
                            <Pastille texte="Proposée" ton="success" />
                          ) : (
                            <Pastille texte="Désactivée" ton="neutral" />
                          )}
                          {formation.revisionProgramme && <Pastille texte="Révision en cours" ton="warning" />}
                        </div>
                      </td>

                      {/* Colonne Actions rapides */}
                      <td className="px-5 py-4 align-top text-right">
                        <div className="inline-flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => basculerActif(formation)}
                            disabled={enEcriture}
                            title={
                              formation.actif
                                ? 'Désactiver (ne plus proposer cette formation)'
                                : 'Réactiver cette formation'
                            }
                            aria-label={
                              formation.actif
                                ? `Désactiver la formation ${formation.titre}`
                                : `Réactiver la formation ${formation.titre}`
                            }
                            className={`flex h-8 w-8 items-center justify-center rounded-lg border transition disabled:opacity-40 ${
                              formation.actif
                                ? 'border-gray-200 bg-white text-codialis-gray hover:border-gray-300 hover:text-codialis-navy'
                                : 'border-codialis-mint bg-codialis-mint/20 text-codialis-navy hover:bg-codialis-mint/30'
                            }`}
                          >
                            <Power className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>

                          <button
                            type="button"
                            onClick={() => setCalendrierCibleId(formation.id)}
                            title="Modifier la programmation"
                            aria-label={`Programmer le calendrier pour ${formation.titre}`}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-codialis-navy transition hover:border-gray-300 hover:bg-gray-50 disabled:opacity-40"
                          >
                            <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>

                          <button
                            type="button"
                            onClick={() => setEdition({ brouillon: versBrouillon(formation), id: formation.id })}
                            disabled={enEcriture}
                            title="Régler la formation"
                            aria-label={`Régler la formation ${formation.titre}`}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-codialis-navy transition hover:border-gray-300 hover:bg-gray-50 disabled:opacity-40"
                          >
                            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>

                          <button
                            type="button"
                            onClick={() => setASupprimer(formation)}
                            disabled={
                              enEcriture ||
                              formation.socle ||
                              formation.rattachements.total > 0
                            }
                            title={
                              formation.socle
                                ? 'Formation socle livrée avec la plateforme : non supprimable'
                                : formation.rattachements.total > 0
                                ? 'Formation rattachée à des dossiers, sessions ou cours : désactivez-la'
                                : 'Supprimer la formation'
                            }
                            aria-label={`Supprimer la formation ${formation.titre}`}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-red-600 transition hover:border-red-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30"
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Tiroir / Détail inline des modules si une formation est dépliée */}
        {formationDepliee && (
          <TiroirProgramme
            formation={formationDepliee}
            lienIngenierie={lienIngenierie}
            onFermer={() => setFormationDeplieeId(null)}
          />
        )}
      </div>

      {/* Note d'information réglementaire Codialis */}
      <div className="flex items-start gap-3 rounded-2xl bg-codialis-sand p-4 text-xs text-codialis-gray md:p-5">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-codialis-navy" aria-hidden="true" />
        <p className="leading-relaxed">
          <strong className="font-semibold text-codialis-navy">Règles administratives et BPF :</strong> Le programme (intitulé, niveau, codes, volumes, blocs) se construit et se valide dans « Ingénierie pédagogique » ; une révision n’y change rien ici avant sa validation. Le code d’une formation est figé afin de préserver l’intégrité des dossiers, contrats, séances et sessions déjà rattachés. Une formation engagée se désactive au lieu de se supprimer. Le niveau porté au BPF se déduit du libellé de qualification entre parenthèses.
        </p>
      </div>

        </>
      )}

      {/* Fenêtre modale d'ajout / modification */}
      {edition && (
        <ModaleFormation
          brouillon={edition.brouillon}
          lienProgramme={lienIngenierie ? `/ped-ingenierie?programme=${encodeURIComponent(edition.id)}` : null}
          occupe={enEcriture}
          onChange={(brouillon) => setEdition({ ...edition, brouillon })}
          onFermer={() => setEdition(null)}
          onEnregistrer={enregistrer}
          onOuvrirCalendrier={() => setCalendrierCibleId(edition.id)}
        />
      )}

      {/* Fenêtre modale de confirmation de suppression */}
      <Modale
        titre="Supprimer cette formation ?"
        ouverte={aSupprimer !== null}
        onFermer={() => setASupprimer(null)}
        largeur="max-w-lg"
      >
        {aSupprimer && (
          <div className="space-y-4 px-4 sm:px-6 py-5">
            <p className="text-sm text-codialis-gray leading-relaxed">
              La formation <strong className="font-semibold text-codialis-navy">{aSupprimer.titre}</strong> ({aSupprimer.code}) et son programme seront définitivement effacés. La suppression n’est autorisée que si la formation n’a jamais été rattachée à aucun dossier.
            </p>
            {(aSupprimer.rattachements.total > 0 || aSupprimer.socle) && (
              <MessageErreur
                message={
                  aSupprimer.rattachements.total > 0
                    ? `${aSupprimer.rattachements.dossiers} dossier(s), ${aSupprimer.rattachements.sessions} session(s) et ${aSupprimer.rattachements.contrats} contrat(s) visent cette formation : la suppression est refusée. Désactivez-la plutôt.`
                    : 'Formation socle de la plateforme : elle ne peut pas être supprimée. Désactivez-la si vous ne souhaitez plus la proposer.'
                }
              />
            )}
            <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
              <BoutonSecondaire onClick={() => setASupprimer(null)}>Annuler</BoutonSecondaire>
              <BoutonPrincipal
                onClick={() =>
                  supprimer.mutate(aSupprimer.id, {
                    onSuccess: () => {
                      setASupprimer(null);
                      if (formationDeplieeId === aSupprimer.id) {
                        setFormationDeplieeId(null);
                      }
                      toast('Formation supprimée avec succès');
                    },
                  })
                }
                disabled={enEcriture || aSupprimer.rattachements.total > 0 || aSupprimer.socle}
              >
                Supprimer la formation
              </BoutonPrincipal>
            </div>
          </div>
        )}
      </Modale>

      {calendrierCibleId && (
        <ModaleCalendrier
          formationId={calendrierCibleId}
          onFermer={() => setCalendrierCibleId(null)}
        />
      )}
    </div>
  );
}

/** Tiroir d'affichage du programme détaillé d'une formation. */
function TiroirProgramme({
  formation,
  lienIngenierie,
  onFermer,
}: {
  formation: FormationParametree;
  lienIngenierie: boolean;
  onFermer: () => void;
}): JSX.Element {
  const ecart = formation.heuresModules - formation.heures;

  return (
    <div id="programme-formation" className="scroll-mt-24 border-t border-gray-200 bg-gray-50/90 p-4 sm:p-5 transition-all">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-codialis-navy" aria-hidden="true" />
          <h3 className="font-display text-sm font-bold text-codialis-navy">
            Détail du programme &bull; {formation.titre} ({formation.code})
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {formation.revisionProgramme && <Pastille texte="Révision en cours" ton="warning" />}
          {lienIngenierie && (
            <Link
              to={`/ped-ingenierie?programme=${encodeURIComponent(formation.id)}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-codialis-navy shadow-xs transition hover:bg-gray-50"
            >
              <Pencil className="h-3 w-3" aria-hidden="true" />
              Réviser dans « Ingénierie pédagogique »
            </Link>
          )}
          <button
            type="button"
            onClick={onFermer}
            aria-label="Fermer le détail du programme"
            className="rounded-lg p-1.5 text-codialis-gray transition hover:bg-gray-200/60 hover:text-codialis-navy"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {ecart !== 0 && formation.modules.length > 0 && (
        <div className="mb-3">
          <Avertissement>
            Volume au programme : <strong>{formation.heuresModules} h</strong> pour{' '}
            <strong>{formation.heures} h</strong> annoncées au total ({ecart > 0 ? `+${ecart}` : ecart} h). Le certificat de réalisation n’atteste que les heures réelles émargées.
          </Avertissement>
        </div>
      )}

      {formation.modules.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-200 bg-white p-4 text-xs text-codialis-gray italic text-center">
          Aucun module n’a encore été saisi pour cette formation : son programme se révise dans « Ingénierie pédagogique ».
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {formation.modules.map((module, index) => (
            <div
              key={module.id}
              className="flex flex-col justify-between rounded-xl border border-gray-200/80 bg-white p-3.5 shadow-xs"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-mono text-[10px] font-bold text-codialis-gray uppercase tracking-wider">
                  Module {index + 1}
                </span>
                <span className="rounded-full bg-codialis-sky/10 px-2 py-0.5 text-xs font-bold text-codialis-sky">
                  {module.heures} h
                </span>
              </div>
              <p className="mt-2 text-xs font-medium text-codialis-navy leading-relaxed">
                {module.libelle}
              </p>
              {module.devoirs > 0 && (
                <span className="mt-2 text-[10px] text-codialis-gray">
                  {module.devoirs} devoir(s) associé(s)
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Contrôle de filtre par segments exclusifs. */
function Segments<T extends string>({
  libelle,
  valeur,
  options,
  onChange,
}: {
  libelle: string;
  valeur: T;
  options: { valeur: T; libelle: string }[];
  onChange: (valeur: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label={libelle}
      className="inline-flex rounded-xl border border-gray-200 bg-white p-1"
    >
      {options.map((option) => (
        <button
          key={option.valeur}
          type="button"
          onClick={() => onChange(option.valeur)}
          aria-pressed={valeur === option.valeur}
          className={`min-h-[34px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg px-3 text-xs font-semibold transition ${
            valeur === option.valeur
              ? 'bg-codialis-navy text-white shadow-xs'
              : 'text-codialis-gray hover:bg-gray-50 hover:text-codialis-navy'
          }`}
        >
          {option.libelle}
        </button>
      ))}
    </div>
  );
}

function Avertissement({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-start gap-2 rounded-xl bg-amber-50 px-3.5 py-2.5 text-xs text-amber-800">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

/**
 * Fenêtre des réglages d'une formation : cycle, horaires, code d'émargement,
 * disponibilité. Le programme s'y lit ; il se révise dans « Ingénierie
 * pédagogique » (TF-472).
 */
function ModaleFormation({
  brouillon,
  lienProgramme,
  occupe,
  onChange,
  onFermer,
  onEnregistrer,
  onOuvrirCalendrier,
}: {
  brouillon: BrouillonFormation;
  /** Lien vers le programme dans « Ingénierie pédagogique », pour qui y a accès. */
  lienProgramme: string | null;
  occupe: boolean;
  onChange: (brouillon: BrouillonFormation) => void;
  onFermer: () => void;
  onEnregistrer: () => void;
  onOuvrirCalendrier: () => void;
}): JSX.Element {
  const modifier = (champs: Partial<BrouillonFormation>): void => onChange({ ...brouillon, ...champs });
  const apercuHoraires = apercuDureeHebdomadaire(brouillon.horaires);
  const modifierHoraires = (champs: Partial<HorairesSaisis>): void =>
    modifier({ horaires: { ...brouillon.horaires, ...champs } });

  const heuresModules = brouillon.modules.reduce(
    (somme, module) => somme + (typeof module.heures === 'number' ? module.heures : 0),
    0,
  );
  const erreurHoraires = apercuHoraires && 'erreur' in apercuHoraires ? apercuHoraires.erreur : null;

  return (
    <Modale
      titre={`Réglages de la formation · ${brouillon.code}`}
      sousTitre="Cycle, horaires, émargement et disponibilité. Le programme se révise dans « Ingénierie pédagogique »."
      ouverte
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-6 px-4 sm:px-6 py-5">
        {/* Programme validé, en lecture : il se révise dans « Ingénierie pédagogique ». */}
        <Section titre={`Programme validé · ${heuresModules} h de blocs`}>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {(
              [
                ['Intitulé', brouillon.titre],
                ['Niveau', brouillon.niveau],
                ['Code diplôme', brouillon.codeDiplome],
                ['RNCP', brouillon.rncp],
                ['Volume annoncé', `${brouillon.heures} h`],
                ['Écrits · ECF', `${brouillon.heuresEcrits || 0} h · ${brouillon.heuresEcf || 0} h`],
                [
                  'Seuil absences',
                  `${brouillon.seuilAbsencesDemiJournees === '' ? 5 : brouillon.seuilAbsencesDemiJournees} demi-journées (réglé dans « Paramétrage »)`,
                ],
              ] as const
            ).map(([libelle, valeur]) => (
              <div key={libelle} className="min-w-0">
                <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">{libelle}</dt>
                <dd className="text-codialis-navy">{valeur}</dd>
              </div>
            ))}
          </dl>
          {brouillon.modules.length > 0 && (
            <ol className="space-y-1.5">
              {brouillon.modules.map((module, index) => (
                <li
                  key={module.id ?? index}
                  className="flex items-start justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2 text-xs text-codialis-navy"
                >
                  <span className="min-w-0">
                    <span className="font-semibold">Bloc {index + 1} · </span>
                    {module.libelle}
                  </span>
                  <span className="shrink-0 font-semibold">{module.heures === '' ? 0 : module.heures} h</span>
                </li>
              ))}
            </ol>
          )}
          <p className="flex items-start gap-2 rounded-xl bg-codialis-sand p-3 text-xs text-codialis-navy">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-codialis-gray" aria-hidden="true" />
            <span>
              Intitulé, niveau, codes, volumes et blocs se modifient dans une révision du programme, validée par le
              pôle pédagogique.{' '}
              {lienProgramme && (
                <Link to={lienProgramme} className="font-semibold text-codialis-sky hover:underline">
                  Ouvrir le programme dans « Ingénierie pédagogique »
                </Link>
              )}
            </span>
          </p>
        </Section>

        {/* Section : Cycle et horaires */}
        <Section titre="Durée du cycle & horaires">
          <Champ
            libelle="Durée de la formation"
            commeBloc
            aide="Durée théorique, commune à toutes les sessions. Elle fixe le nombre d’années de rémunération et l’étendue du calendrier ; les dates réelles se règlent sur chaque session."
          >
            <div className="flex flex-wrap gap-2" role="group" aria-label="Durée de la formation">
              {DUREES_CYCLE.map((duree) => (
                <Bascule
                  key={duree}
                  actif={brouillon.dureeAnnees === duree}
                  onClick={() => modifier({ dureeAnnees: brouillon.dureeAnnees === duree ? '' : duree })}
                >
                  {libelleDuree(duree)}
                </Bascule>
              ))}
            </div>
          </Champ>
          {brouillon.dureeAnnees === '' ? (
            <p className="text-xs text-codialis-gray">
              Durée non renseignée : le calendrier se programme sur une année, et le contrôle du
              CERFA ne peut pas comparer la durée du contrat à celle de la formation.
            </p>
          ) : (
            <p className="text-xs text-codialis-navy">
              <span className="font-semibold">{libelleDuree(brouillon.dureeAnnees)}</span> ·{' '}
              {Array.from({ length: brouillon.dureeAnnees }, (_, i) => libelleRangAnnee(i + 1)).join(
                ' · ',
              )}
            </p>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {(
              [
                ['heureDebutMatin', 'Matin · début'],
                ['heureFinMatin', 'Matin · fin'],
                ['heureDebutApresMidi', 'Après-midi · début'],
                ['heureFinApresMidi', 'Après-midi · fin'],
              ] as const
            ).map(([cle, libelle]) => (
              <Champ key={cle} libelle={libelle}>
                <input
                  type="time"
                  value={brouillon.horaires[cle]}
                  onChange={(evenement) => modifierHoraires({ [cle]: evenement.target.value })}
                  className={CHAMP_TEXTE}
                />
              </Champ>
            ))}
            <Champ libelle="Jours / semaine">
              <input
                type="number"
                min={1}
                max={6}
                inputMode="numeric"
                value={brouillon.horaires.joursParSemaine}
                onChange={(evenement) =>
                  modifierHoraires({
                    joursParSemaine:
                      evenement.target.value === '' ? '' : Number(evenement.target.value),
                  })
                }
                placeholder="5"
                className={CHAMP_TEXTE}
              />
            </Champ>
          </div>
          <div
            className={`rounded-xl px-3.5 py-2.5 text-sm ${
              erreurHoraires ? 'bg-red-50 text-red-700' : 'bg-codialis-sand text-codialis-navy'
            }`}
            aria-live="polite"
          >
            {apercuHoraires === null ? (
              <span className="text-codialis-gray">
                Horaires non renseignés : l’émargement garde ses horaires par défaut (08:00–12:00,
                13:30–17:00) et la durée hebdomadaire se saisit au CERFA.
              </span>
            ) : 'erreur' in apercuHoraires ? (
              apercuHoraires.erreur
            ) : (
              <>
                Durée hebdomadaire :{' '}
                <span className="font-bold">{libelleDureeHebdomadaire(apercuHoraires)}</span>
                <span className="block text-xs text-codialis-gray">
                  Proposée au CERFA de chaque contrat de la formation, et créneaux de l’émargement.
                </span>
              </>
            )}
          </div>
        </Section>

        {/* Section : Émargement */}
        <Section titre="Émargement">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Champ
              libelle="Code d’émargement"
              aide={
                brouillon.codeEmargementDefini
                  ? 'Défini · saisir quatre chiffres pour le remplacer'
                  : 'Quatre chiffres · demandés pour quitter le mode signature'
              }
            >
              <input
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                maxLength={4}
                value={brouillon.codeEmargement}
                disabled={brouillon.retirerCodeEmargement}
                onChange={(evenement) =>
                  modifier({ codeEmargement: evenement.target.value.replace(/\D/g, '') })
                }
                placeholder={brouillon.codeEmargementDefini ? '••••' : '0000'}
                className={`${CHAMP_TEXTE} tracking-[0.4em]`}
              />
            </Champ>
            <div className="space-y-1.5 text-xs text-codialis-gray sm:pt-6">
              <p>
                Code par défaut de toutes les sessions de la formation. Une session peut porter
                le sien, qui l’emporte. Il n’est jamais réaffiché : on le remplace.
              </p>
              {brouillon.codeEmargementDefini && (
                <label className="inline-flex min-h-[40px] items-center gap-2 text-codialis-navy">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    checked={brouillon.retirerCodeEmargement}
                    onChange={(evenement) =>
                      modifier({
                        retirerCodeEmargement: evenement.target.checked,
                        codeEmargement: '',
                      })
                    }
                  />
                  Retirer le code (le formateur en choisira un à l’appel)
                </label>
              )}
            </div>
          </div>
        </Section>

        {/* Section : Disponibilité */}
        <Section titre="Disponibilité">
          <Champ
            libelle="Statut de la formation"
            aide="Désactivée, elle n’apparaît plus sur les nouveaux dossiers ; les dossiers existants la conservent."
            commeBloc
          >
            <Bascule actif={brouillon.actif} onClick={() => modifier({ actif: !brouillon.actif })}>
              {brouillon.actif ? 'Formation proposée aux dossiers' : 'Formation désactivée'}
            </Bascule>
          </Champ>
        </Section>

        {erreurHoraires && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            Des horaires cohérents sont attendus : {erreurHoraires}
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
          <div>
            <BoutonSecondaire onClick={onOuvrirCalendrier}>
              <Calendar className="mr-2 h-4 w-4" aria-hidden />
              Programmer le calendrier
            </BoutonSecondaire>
          </div>
          <div className="flex gap-3">
            <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
            <BoutonMint onClick={onEnregistrer} disabled={occupe || erreurHoraires !== null}>
              Enregistrer les réglages
            </BoutonMint>
          </div>
        </div>
      </div>
    </Modale>
  );
}

const CHAMP_TEXTE =
  'w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm text-codialis-navy placeholder:text-gray-400 focus:border-codialis-sky focus:ring-2 focus:ring-codialis-sky/20 focus:outline-none transition min-h-[40px]';

function Section({ titre, children }: { titre: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="space-y-3.5">
      <h3 className="text-xs font-bold uppercase tracking-wider text-codialis-gray border-b border-gray-100 pb-1.5">
        {titre}
      </h3>
      {children}
    </section>
  );
}

function Champ({
  libelle,
  obligatoire = false,
  aide,
  children,
  commeBloc = false,
}: {
  libelle: string;
  obligatoire?: boolean;
  aide?: string;
  children: React.ReactNode;
  commeBloc?: boolean;
}): JSX.Element {
  const Enveloppe = commeBloc ? 'div' : 'label';
  return (
    <Enveloppe className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
        {obligatoire && (
          <span className="ml-1 text-red-500 font-bold" title="Champ obligatoire">
            *
          </span>
        )}
      </span>
      {children}
      {aide && <span className="mt-1 block text-[11px] text-codialis-gray">{aide}</span>}
    </Enveloppe>
  );
}

function Bascule({
  actif,
  onClick,
  children,
}: {
  actif: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actif}
      className={`min-h-[40px] rounded-xl px-4 text-xs font-semibold transition border ${
        actif
          ? 'border-codialis-mint bg-codialis-mint text-codialis-navy font-bold'
          : 'border-gray-200 bg-white text-codialis-gray hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}
